import { useQuery } from '@tanstack/react-query';

import {
  useRequest,
  type QueryFunction,
  type BasePartCardData,
} from '@frontend/api';

import type { Resolution } from './types';

type QueryParams = {
  ruleUuid: string;
  actionUuid?: string;
  quoteId?: number;
};

type ActionSimilaritySearchComponent = {
  id: number;
  quoteItemId: number;
  quoteNumberRevisionNumber: string;
  quoteStatus: string;
  selectedResolution: Resolution;
};

export type ActionSimilaritySearchPart = BasePartCardData & {
  partNumber: string;
  revision: string;
  description: string;
  isAssembly: boolean;
  isExportControlled: boolean;
  isRootPart: boolean;
  latestComponent: ActionSimilaritySearchComponent;
};

type ActionSimilarityResponse = {
  thisQuoteParts: ActionSimilaritySearchPart[];
  otherQuoteParts: ActionSimilaritySearchPart[];
};

export const getUseGetActionSimilarPartsKey = (query: QueryParams) => [
  'actions',
  'similarParts',
  query.ruleUuid,
  query.actionUuid,
  query.quoteId,
];

export const useGetActionSimilarParts: QueryFunction<
  ActionSimilarityResponse,
  [QueryParams],
  ReturnType<typeof getUseGetActionSimilarPartsKey>
> = (params, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetActionSimilarPartsKey(params),
    queryFn: ({ signal }) => {
      return request<ActionSimilarityResponse>(
        `/rules/${params.ruleUuid}/similar/`,
        {
          query: {
            actionUuid: params.actionUuid,
            quoteId: params.quoteId,
          },
          init: {
            signal,
          },
        },
      );
    },
  });
};
